import { Component, ChangeDetectionStrategy } from '@angular/core';
import Document from '../../interfaces/document';
import { DocumentTrackingService } from './document-tracking.service';
import { BehaviorSubject, combineLatest, debounce, debounceTime, firstValueFrom, Subject, Subscription } from "rxjs";
import DocType from '../../interfaces/doc-type';
import DocSubtype from '../../interfaces/doc-subtype';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import DocStatus from '../../interfaces/doc-status';
import { DataFormatService } from '../../services/data-format.service';
import TaxReturnDifficultyLevel from '../../interfaces/tax-return-difficulty-levels';
import ID from '../../types/id';
import { FormControl, FormGroup } from '@angular/forms';
import {
  DocumentFilter,
  UpdateDocumentInput,
  WhereColumn,
  WhereConditions,
} from './graphql/document-tracking';
import VDocumentsFilteringTable from '../../interfaces/v-documents-filtering-table';
import { MatDialog } from '@angular/material/dialog';
import { DefaultSnackBarService } from '../../services/default-snack-bar.service';
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component';
import SqlOperator from '../../enums/sql-operator';
import DateInput from '../../types/date-input';
import { formatDate } from '@angular/common';
import { AddDocumentDialogComponent } from './add-document-dialog/add-document-dialog.component';
import Permission from '../../interfaces/permission';

enum DocumentEntityType {
  FUND,
  PROJECT,
  OTHER,
}
type DocumentTableItem = {
  expanded: boolean;
  document: Document;
  entity_name: string | null;
  related_name: string | null;
  related_url: string | null;
  difficulty_level: string | null;
  status: string | null;
  type: string;
  am_name: string | null;
  am_url: string | null;
  auditor_url: string | null;
  auditor_name: string | null;
  due_date: string | null;
  assess_fees: boolean;
  last_followup_sent: string | null;
  expected_date: string | null;
  note_text: string | null;
  addingNote: boolean;
  document_version_id: ID | null;
  newNoteText?: string;
  permissions: Permission | null;
  statuses: DocStatus[];
};
@Component({
    selector: 'document-tracking',
    templateUrl: './document-tracking.component.html',
    styleUrls: ['./document-tracking.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentTrackingComponent {
  public columns = [
    'expand_collapse',
    'entity_name',
    'asset_manager',
    'difficulty',
    'auditor',
    'type',
    'period',
    'version',
    'status',
    'status_date',
    'user',
    'due_date',
    'followup_date',
    'expected_date',
    'assess_fees',
    'notes',
    'delete',
  ];
  public docSub: Subscription;
  private _items: DocumentTableItem[] = [];
  private items = new BehaviorSubject(this._items);
  public items$ = this.items.asObservable();
  private filterSub: Subscription;
  private docUpdateSub: Subscription;
  private taxReturnDifficultyLevels: TaxReturnDifficultyLevel[] = [];
  private docTypes: DocType[] = [];
  private docSubTypes: DocSubtype[] = [];
  private docStatuses: DocStatus[] = [];
  private formSub: Subscription;
  private timeOuts: { timer: any; document_id: ID; key: string }[] = [];
  public rowChange = new Subject<{
    event: Event, document: Document, key: string
  }|null>();
  private rowChangeSub: Subscription;
  public dueDateChange = new Subject<{event:Event, document:Document}|null>();
  private dueDateChangeSub: Subscription;
  public formFilters = new FormGroup({
    entity_name: new FormControl<string | null>(null),
    doc_type_id: new FormControl<ID[] | null>(null),
    current_doc_subtype_id: new FormControl<ID[] | null>(null),
    current_doc_status_id: new FormControl<ID[] | null>(null),
    doc_date_from: new FormControl<string | Date | null>(null),
    doc_date_to: new FormControl<string | Date | null>(null),
    due_date_from: new FormControl<string | Date | null>(null),
    due_date_to: new FormControl<string | Date | null>(null),
    tax_return_difficult_level_id: new FormControl<ID[] | null>(null),
    asset_manager_name: new FormControl<string | null>(null),
    auditor_name: new FormControl<string | null>(null),
    doc_group_id: new FormControl<ID[] | null>(null),
    sort: new FormControl<string | null>(null),
  });
  constructor(
    public documentsTrackerService: DocumentTrackingService,
    private dataFormat: DataFormatService,
    public dialog: MatDialog,
    private _snackBar: DefaultSnackBarService
  ) {}
  ngOnInit() {
    this.loadCachedFilters();
    this.documentsTrackerService.fetchDocuments();
    this.docSub = combineLatest({
      documents: this.documentsTrackerService.documents$,
      docTypes: this.documentsTrackerService.docTypes$,
      docSubtypes: this.documentsTrackerService.docSubtypes$,
      docStatuses: this.documentsTrackerService.docStatuses$,
      taxReturnDifficultyLevels:
        this.documentsTrackerService.taxReturnDifficultyLevels$,
    }).subscribe(
      ({
        documents,
        docTypes,
        docSubtypes,
        docStatuses,
        taxReturnDifficultyLevels,
      }) => {
        this.docTypes = docTypes;
        this.docStatuses = docStatuses;
        this.docSubTypes = docSubtypes;
        this.taxReturnDifficultyLevels = taxReturnDifficultyLevels;
        this.prepareDocumentTableItems(
          documents,
          docTypes,
          docSubtypes,
          docStatuses,
          taxReturnDifficultyLevels
        );
      }
    );
    this.docUpdateSub = this.documentsTrackerService.updatedDocument.subscribe(
      (document) => {
        this.updateItem(document);
      }
    );
    this.rowChangeSub = this.rowChange.pipe(debounceTime(500)).subscribe((change)=> {
        if(change){
          this.saveSimpleValue(change.event, change.document, change.key);
        }
    });
    this.dueDateChangeSub = this.dueDateChange.pipe(debounceTime(500)).subscribe((change)=>{
        if(change){
          this.updateDueDate(change.event, change.document);
        }
    })
  }
  loadCachedFilters(){
    let filters = localStorage.getItem('documentFilters');
    if(filters){
      let filterSet = JSON.parse(filters);
      this.formFilters.setValue(filterSet);
      this.parseFilters()
    }
  }
  applyFiltersOnChange(event:KeyboardEvent){
    if(event.code === 'Enter'){
      this.processFilters();
    }
  }
  getPage(page: number) {
    this.documentsTrackerService.updatePage(page);
    this.documentsTrackerService.refreshDocuments();
  }
  parseFilters(){
    let data = { ...this.formFilters.value };
    let where: WhereConditions | null = null;
    let filter: DocumentFilter = {};
    if (data.entity_name?.length) {
      where = {
        OR: [
          {
            column: WhereColumn.FUND_NAME,
            operator: SqlOperator.LIKE,
            value: `%${data.entity_name}%`,
          },
          {
            column: WhereColumn.PROJECT_NAME,
            operator: SqlOperator.LIKE,
            value: `%${data.entity_name}%`,
          },
          {
            column: WhereColumn.OWNED_ENTITY_NAME,
            operator: SqlOperator.LIKE,
            value: `%${data.entity_name}%`,
          },
        ],
      };
    }
    delete data.entity_name;
    if (data.doc_date_from && data.doc_date_to) {
      filter.doc_date = {
        to: formatDate(data.doc_date_from, 'yyyy-MM-dd', 'en-US'),
        from: formatDate(data.doc_date_to, 'yyyy-MM-dd', 'en-US'),
      };
    }
    if (data.due_date_from && data.due_date_to) {
      filter.due_date = {
        to: formatDate(data.due_date_from, 'yyyy-MM-dd', 'en-US'),
        from: formatDate(data.due_date_to, 'yyyy-MM-dd', 'en-US'),
      };
    }
    if (data.asset_manager_name?.length) {
      filter.asset_manager_name = `%${data.asset_manager_name}%`;
    }
    if (data.current_doc_status_id) {
      filter.current_status_id = data.current_doc_status_id;
    }
    if (data.current_doc_subtype_id) {
      filter.current_doc_subtype_id = data.current_doc_subtype_id;
    }
    if (data.doc_type_id) {
      filter.doc_type_id = data.doc_type_id;
    }
    if (data.auditor_name?.length) {
      filter.auditor_name = `%${data.auditor_name}%`;
    }
    if (data.doc_group_id) {
      filter.doc_group_id = data.doc_group_id;
    }
    if (data.tax_return_difficult_level_id) {
      filter.tax_return_difficulty_level_id =
        data.tax_return_difficult_level_id;
    }
    this.documentsTrackerService.setOrder(data.sort ?? null);
    this.documentsTrackerService.updatePage(1);
    this.documentsTrackerService.updateWhere(where);
    this.documentsTrackerService.updateFilters(filter);
  }
  processFilters() {
    let data = { ...this.formFilters.value };
    localStorage.setItem('documentFilters', JSON.stringify(data));
    this.parseFilters();
    this.documentsTrackerService.refreshDocuments();
  }
  clearFilters() {
    this.formFilters.reset();
    let data = {...this.formFilters.value};
    localStorage.setItem('documentFilters', JSON.stringify(data));
    this.documentsTrackerService.updateFilters({});
    this.documentsTrackerService.updatePage(1);
    this.documentsTrackerService.updateWhere(null);
    this.documentsTrackerService.refreshDocuments();
  }
  public openCreateModal() {
    this.dialog.open(AddDocumentDialogComponent, { data: {} });
  }
  private getRelatedURL(document: VDocumentsFilteringTable): string | null {
    if (document.project_name) {
      return this.dataFormat.formatLink({
        id: document.project_id,
        name: document.project_name,
        __typename: 'Project',
      });
    } else if (document.fund_name) {
      return this.dataFormat.formatLink({
        id: document.fund_id,
        name: document.fund_name,
        __typename: 'Fund',
      });
    }
    return null;
  }
  private getDifficultyLevel(
    document: Document,
    taxReturnDifficultyLevels: TaxReturnDifficultyLevel[]
  ): string | null {
    return (
      taxReturnDifficultyLevels.find(
        (difficultyLevel) =>
          difficultyLevel.id ===
          document.ownedEntity?.tax_return_difficulty_level_id
      )?.difficulty_level ?? null
    );
  }
  private _getStatuses(document: Document) {
    let statuses: DocStatus[] = [];
    let docType = this.docTypes.find(
      (iDocType) => iDocType.id === document.doc_type_id
    );
    if (docType) {
      let subType = docType.docSubtypes.find(
        (iSubType) => iSubType.id === document.current_doc_subtype_id
      );
      if (subType) {
        let filtered = docType.docTypeSubtypeStatuses
          .filter(
            (docStatusType) => docStatusType.doc_subtype_id === subType?.id
          )
          .sort((a, b) => a.sort_order - b.sort_order);
        statuses = filtered.map((docStatusType) => docStatusType.docStatus);
      }
    }
    return statuses;
  }
  private _formatDoc(document: VDocumentsFilteringTable): DocumentTableItem {
    return {
      expanded: false,
      document: document.document,
      related_name: document.project_name ?? document.fund_name,
      entity_name: document.owned_entity_name,
      related_url: this.getRelatedURL(document),
      difficulty_level: document.tax_return_difficulty_level,
      type: this.getDocumentConcatType(
        document,
        this.docTypes,
        this.docSubTypes
      ),
      status: this.getDocumentStatus(document, this.docStatuses),
      am_name: document.asset_manager_name,
      am_url: document.asset_manager_id
        ? this.dataFormat.formatLink({
            id: document.asset_manager_id,
            name: document.asset_manager_name,
            __typename: 'Person',
          })
        : null,
      auditor_url: document.auditor_id
        ? this.dataFormat.formatLink({
            id: document.auditor_id,
            name: document.auditor_name,
            __typename: 'Organization',
          })
        : null,
      auditor_name: document.auditor_name,
      due_date: document.due_date,
      assess_fees: document.assess_fees,
      last_followup_sent: document.last_followup_sent,
      expected_date: document.expected_date,
      note_text: document.note_text,
      document_version_id: document.document_version_id,
      addingNote: false,
      permissions:
        this.docTypes.find((docType) => document.doc_type_id === docType.id)
          ?.permissions ?? null,
      statuses: this._getStatuses(document.document),
    };
  }
  public saveSimpleValue(event: Event, document: Document, key: string) {
    let target = event.target as HTMLInputElement;
    let input: UpdateDocumentInput = {};
    let objKey: keyof UpdateDocumentInput = key as keyof UpdateDocumentInput;
    if (
      objKey === 'last_followup_sent' ||
      objKey === 'expected_date' ||
      objKey === 'current_status_date'
    ) {
      if (target?.value?.length === 10) {
        input[objKey] = target.value;
      } else if (!target?.value?.length) {
        input[objKey] = null;
      }
    }
    this.documentsTrackerService.updateDocument(document.id, input);
  }
  public updateDueDate(event: Event, document: Document) {
    let target = event.target as HTMLInputElement;
    if (target.value?.length === 10) {
      this.documentsTrackerService.updateDueDate(
        document.id,
        document.current_doc_subtype_id,
        target.value
      );
    }
  }
  private updateItem(document: VDocumentsFilteringTable) {
    let item: DocumentTableItem = this._formatDoc(document);
    let index = this._items.findIndex(
      (docItem) => item.document.id === docItem.document.id
    );
    if (index > -1) {
      this._items[index] = item;
      this.items.next(this._items);
    }
  }
  private prepareDocumentTableItems(
    documents: Paginator<VDocumentsFilteringTable, PaginatorInfo>,
    docTypes: DocType[],
    docSubtypes: DocSubtype[],
    docStatuses: DocStatus[],
    taxReturnDifficultyLevels: TaxReturnDifficultyLevel[]
  ) {
    let _items: DocumentTableItem[] = [];
    for (let i = 0; i < documents.data.length; i++) {
      let item: DocumentTableItem;
      let document = documents.data[i];
      item = this._formatDoc(document);
      _items.push(item);
    }
    this._items = _items;
    this.items.next(this._items);
  }
  private getDocumentStatus(
    document: VDocumentsFilteringTable,
    docStatuses: DocStatus[]
  ): string | null {
    return (
      docStatuses.find(
        (docStatus) => docStatus.id === document.current_doc_status_id
      )?.name ?? null
    );
  }
  private getDocumentConcatType(
    document: VDocumentsFilteringTable,
    docTypes: DocType[],
    docSubtypes: DocSubtype[]
  ): string {
    let type = docTypes.find((docType) => docType.id === document.doc_type_id);
    let subType = docSubtypes.find(
      (docSubtype) => docSubtype.id === document.current_doc_subtype_id
    );

    return subType?.name + ' ' + type?.name;
  }
  private getDocumentName(document: VDocumentsFilteringTable): string | null {
    let type = this.getDocumentEntityType(document);
    switch (type) {
      case DocumentEntityType.FUND:
        return document.fund_name ?? null;
      case DocumentEntityType.PROJECT:
        return document.project_name ?? null;
    }
    return null;
  }
  private getDocumentEntityType(
    document: VDocumentsFilteringTable
  ): DocumentEntityType {
    if (document.fund_name) {
      return DocumentEntityType.FUND;
    }
    if (document.project_name) {
      return DocumentEntityType.PROJECT;
    }
    return DocumentEntityType.OTHER;
  }
  public incrementVersion(document: Document) {
    this.documentsTrackerService.updateDocument(document.id, {
      current_version: document.current_version + 1,
    });
  }
  public updateDocStatus(event: Event, item: DocumentTableItem) {
    event.preventDefault();
    event.stopPropagation();
    (async ()=> {
      let target = event.target as HTMLInputElement;
      let status = item.statuses.find((status) => status.id === target.value);
      if (status?.name === 'Approved') {
        let dialog = this.dialog.open(ConfirmationModalComponent, {
          width: '600px',
          height: '240px',
          data: {
            confirmButton:'Approve',
            modalTitle: 'Mark Document Approved',
            actionMessage: 'Marking this document as Approved could trigger automations or lock this document row for editing. Please confirm all information is accurate before proceeding.'
          }
        })
        let data = await firstValueFrom(dialog.afterClosed());
        if (data.clicked === 'Ok') {
          this.documentsTrackerService.updateDocument(item.document.id, {
            current_doc_status_id: target.value,
          });
          return true;
        }
        if(typeof item?.document?.current_doc_status_id === 'string') {
          target.value = item.document.current_doc_status_id;
        }
        return false;
      }
      this.documentsTrackerService.updateDocument(item.document.id, {
        current_doc_status_id: target.value,
      });
      return true;
    })()
  }
  saveNewNote(item: DocumentTableItem) {
    if (item.newNoteText && item.document_version_id) {
      this.documentsTrackerService.addNewNote(
        item.newNoteText,
        item.document_version_id
      );
    }
  }
  checkChecked(event: Event, item: DocumentTableItem) {
    let target = event.currentTarget as HTMLInputElement;
    this.documentsTrackerService.updateDocument(item.document.id, {
      assess_fees: target.checked,
    });
  }

  deleteDocument(document: Document) {
    let dialog = this.dialog.open(ConfirmationModalComponent, {
      data: {
        modalTitle: 'Delete Document',
        actionMessage: '',
        warningMessage:
          'Permanently delete this document with all comments and file history?',
        modalInformation: [],
      },
    });
    dialog.afterClosed().subscribe((data) => {
      if (data?.clicked === 'Ok') {
        let sub: Subscription;
        sub = this.documentsTrackerService
          .deleteDocument(document.id)
          .subscribe((document) => {
            this._snackBar.open('Deleted document successfully');
            this.documentsTrackerService.refreshDocuments();
            sub?.unsubscribe;
          });
      }
    });
  }
  ngOnDestroy() {
    this.docSub?.unsubscribe();
    this.dueDateChangeSub?.unsubscribe();
    this.rowChangeSub?.unsubscribe();
  }

  protected readonly document = document;
}
